import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/PerformanceReport.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useTranslation } from "react-i18next";
import UsageReportData from "../services/UsageReportData";
import Swal from "sweetalert2";

// Custom Tooltip Component with Card Style
const TooltipCard = ({ text, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!text || text.trim() === "") {
    return <>{children}</>;
  }

  return (
    <div
      className="tooltip-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      style={{ 
        position: "relative", 
        display: "inline-block", 
        width: "100%",
        cursor: "pointer"
      }}
    >
      <div className="tooltip-trigger">
        {children}
      </div>
      {showTooltip && (
        <div className="tooltip-card-popup">
          <div className="tooltip-card-body">
            {text}
          </div>
          <div className="tooltip-card-arrow-bottom"></div>
        </div>
      )}
    </div>
  );
};

const ReportHeader = ({ reportType, setReportType, onApply }) => {
  const { t } = useTranslation(); //Multilingual

  return (
    <div className="report-header">
      <div className="header-left">
        <i className="fas fa-chart-line"></i>
        <span className="report-title">{t(`report_titles.${reportType}`)}</span>
      </div>
      <div className="header-right">
        <label
          htmlFor="report-type"
          style={{ marginRight: "10px", fontWeight: "bold", color: "#333333" }}
        >
          {t("report_type_label")}
        </label>
        <select
          id="report-type"
          className="report-type-dropdown"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
        >
          <option value="usage_reports">{t("usage_reports_option")}</option>
        </select>
        <button className="apply-btn" onClick={onApply}>
          {t("apply_button")}
        </button>
      </div>
    </div>
  );
};

// Usage Report Table Component
const UsageReportTable = ({ filteredData }) => {
  const { t } = useTranslation();

  return (
    <div className="usage-report">
      <table className="usage-report-table w-full h-full border-collapse border border-gray-300 bg-white shadow-lg">
        <thead className="bg-gray-100">
          <tr>
            <th
              className="border border-gray-300 px-2 py-3 text-gray-600 text-center font-semibold"
              style={{ width: "50px" }}
            >
              {t("serial_no_label")}
            </th>
            <th className="border border-gray-300 px-6 py-4 text-gray-600 text-left font-semibold">
              {t("plan_name_label")}
            </th>
            <th className="border border-gray-300 px-6 py-4 text-gray-600 text-left font-semibold">
              {t("cost_of_plan_label")}
            </th>
            <th className="border border-gray-300 px-6 py-4 text-gray-600 text-left font-semibold">
              {t("initial_recharge_label")}
            </th>
            <th className="border border-gray-300 px-6 py-4 text-gray-600 text-left font-semibold">
              {t("usage_label")}
            </th>
            <th className="border border-gray-300 px-6 py-4 text-gray-600 text-left font-semibold">
              {t("balance_label")}
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredData.length === 0 ? (
            <tr>
              <td colSpan="6" className="border border-gray-300 px-6 py-8 text-center text-gray-500">
                {t("no_data_available") || "No data available"}
              </td>
            </tr>
          ) : (
            filteredData.map((row, index) => (
              <tr
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f4f4f4" : "white", // Use grey for even rows
                }}
                className="border-t"
              >
                <td
                  className="border border-gray-300 px-2 py-3 text-gray-700 text-center"
                  style={{ width: "50px" }}
                >
                  {index + 1}
                </td>
                <td className="border border-gray-300 px-6 py-4 text-gray-700">
                  {row.planName || "N/A"}
                </td>
                <td className="border border-gray-300 px-6 py-4 text-gray-700">
                  {row.initialPlanCost ? `₹${row.initialPlanCost}` : "₹0"}
                </td>
                <td className="border border-gray-300 px-6 py-4 text-gray-700">
                  <TooltipCard text={`${row.initialCallMinutes || "0"} minutes, ${row.initialChats || "0"} chats`}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {`${row.initialCallMinutes || "0"} minutes, ${row.initialChats || "0"} chats`}
                    </span>
                  </TooltipCard>
                </td>
                <td className="border border-gray-300 px-6 py-4 text-gray-700">
                  <TooltipCard text={row.usageData || "No data available"}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.usageData || "No data available"}
                    </span>
                  </TooltipCard>
                </td>
                <td className="border border-gray-300 px-6 py-4 text-gray-700">
                  <TooltipCard text={row.balanceData || "No data available"}>
                    <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {row.balanceData || "No data available"}
                    </span>
                  </TooltipCard>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Performance Report Component
const UsageReport = ({ serviceName }) => {
  const { t } = useTranslation();

  const [dateRange, setDateRange] = useState([
    new Date(new Date().setMonth(new Date().getMonth() - 1)), // One month ago
    new Date(), // Today
  ]);
  const [startDate, endDate] = dateRange;
  const [reportType, setReportType] = useState("usage_reports");
  const [filteredData, setFilteredData] = useState([]);
  const [content, setContent] = useState(null);
  const [rawUsageReportData, setRawUsageReportData] = useState([
    /*{
            date: new Date("2024-12-29"),
            planNameKey: "basic_plan_name",
            cost: "₹900",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 90,
            usageKey: "minutes_label",
            usageValue: 43,
            balanceKey: "minutes_label",
            balanceValue: 57,
        },
        {
            date: new Date("2024-12-30"),
            planNameKey: "basic_plus_plan_name",
            cost: "₹3600",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 130,
            usageKey: "minutes_label",
            usageValue: 94,
            balanceKey: "minutes_label",
            balanceValue: 36,
        },
        {
            date: new Date("2025-01-02"),
            planNameKey: "pro_plan_name",
            cost: "₹900",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 90,
            usageKey: "minutes_label",
            usageValue: 43,
            balanceKey: "minutes_label",
            balanceValue: 57,
        },
        {
            date: new Date("2025-01-05"),
            planNameKey: "pro_plus_plan_name",
            cost: "₹3600",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 130,
            usageKey: "minutes_label",
            usageValue: 94,
            balanceKey: "minutes_label",
            balanceValue: 36,
        },
        {
            date: new Date("2024-11-15"),
            planNameKey: "basic_plan_name",
            cost: "₹900",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 90,
            usageKey: "minutes_label",
            usageValue: 43,
            balanceKey: "minutes_label",
            balanceValue: 57,
        },
        {
            date: new Date("2024-10-10"),
            planNameKey: "basic_plus_plan_name",
            cost: "₹3600",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 130,
            usageKey: "minutes_label",
            usageValue: 94,
            balanceKey: "minutes_label",
            balanceValue: 36,
        },
        {
            date: new Date("2025-02-20"),
            planNameKey: "pro_plan_name",
            cost: "₹900",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 90,
            usageKey: "minutes_label",
            usageValue: 43,
            balanceKey: "minutes_label",
            balanceValue: 57,
        },
        {
            date: new Date("2025-03-05"),
            planNameKey: "pro_plus_plan_name",
            cost: "₹3600",
            initialRechargeKey: "minutes_label",
            initialRechargeValue: 130,
            usageKey: "minutes_label",
            usageValue: 94,
            balanceKey: "minutes_label",
            balanceValue: 36,
        },*/
  ]);

  // Filter data based on date range
  const handleApply = () => {
    let filtered;

    // Default to showing all data if date range isn't selected
    if (startDate && endDate) {
      // Normalize dates to start of day for proper comparison
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(23, 59, 59, 999); // Include entire end date
      
      filtered = rawUsageReportData.filter((data) => {
        if (!data.date) return false;
        
        // Normalize data date to start of day for comparison
        const dataDate = new Date(data.date);
        dataDate.setHours(0, 0, 0, 0);
        
        // Check if data date is within the selected range (inclusive)
        return dataDate >= normalizedStartDate && dataDate <= normalizedEndDate;
      });
    } else {
      filtered = rawUsageReportData;
    }

    setFilteredData(filtered);

    if (reportType === "usage_reports") {
      setContent(<UsageReportTable filteredData={filtered} />);
    } else if (reportType === "performance_report") {
      setContent(<div>{t("performance_report_data")}</div>);
    } else {
      setContent(<div>{t("no_data_available")}</div>);
    }
  };

  // Initialize filtered data on component mount
  useEffect(() => {
    const fetchUsageReport = async () => {
      try {
        const responseData = await UsageReportData.usageReportDetails(serviceName);

        console.log("**** usage report data" + JSON.stringify(responseData));
       
        // Transform the response data to match expected format
        const transformedData = responseData.map((plan, index) => {
          // Parse orderDate if available, otherwise use current date
          let planDate = new Date();
          if (plan.orderDate) {
            // Try parsing the orderDate - it might be in different formats
            const parsedDate = new Date(plan.orderDate);
            if (!isNaN(parsedDate.getTime())) {
              planDate = parsedDate;
            }
          }
          
          return {
            ...plan,
            // Use orderDate for filtering
            date: planDate,
            // Ensure all required fields are present
            planName: plan.planName || "N/A",
            initialPlanCost: plan.initialPlanCost || "0",
            initialCallMinutes: plan.initialCallMinutes || "0",
            initialChats: plan.initialChats || "0",
            usageData: plan.usageData || "No data available",
            balanceData: plan.balanceData || "No data available"
          };
        });
       
        setRawUsageReportData(transformedData);
        // Filtering will be handled by the useEffect that watches date changes
      } catch (error) {
        console.error("Error fetching usage report datas:", error);
        // Show user-friendly error message
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch usage report data. Please try again later.",
          confirmButtonText: "OK",
        });
      }
    };
    
    if (serviceName) {
      fetchUsageReport();
    }
  }, [serviceName]); // Fetch when serviceName changes
  
  // Apply date filter when date range or raw data changes
  useEffect(() => {
    if (rawUsageReportData.length === 0) return;
    
    if (startDate && endDate) {
      const normalizedStartDate = new Date(startDate);
      normalizedStartDate.setHours(0, 0, 0, 0);
      
      const normalizedEndDate = new Date(endDate);
      normalizedEndDate.setHours(23, 59, 59, 999);
      
      const filtered = rawUsageReportData.filter((data) => {
        if (!data.date) return false;
        
        const dataDate = new Date(data.date);
        dataDate.setHours(0, 0, 0, 0);
        
        return dataDate >= normalizedStartDate && dataDate <= normalizedEndDate;
      });
      
      setFilteredData(filtered);
    } else {
      setFilteredData(rawUsageReportData);
    }
  }, [startDate, endDate, rawUsageReportData]);

  const transformData = (plans) => {
    return plans.map(plan => {
      // Copy the plan as-is
      const transformedPlan = { ...plan };
  
      // Ensure balanceData and usageData are in correct format
      const balanceData = {};
      if (plan.balanceData ) {
        Object.entries(plan.balanceData).forEach(([date, data]) => {
         
          balanceData[date] = {
            CHATS: Number(data.CHATS),
            CALLS: Number(data.CALLS)
          };
        });
      }
  
      const usageData = {};
      if (plan.usageData ) {
        Object.entries(plan.usageData).forEach(([date, data]) => {
          usageData[date] = {
            CHATS: Number(data.CHATS),
            CALLS: Number(data.CALLS)
          };
        });
      }
  
      transformedPlan.balanceData = balanceData;
      transformedPlan.usageData = usageData;
  
      return transformedPlan;
    });
  };
  

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data Found",
        text: t("no_data_export_message") || "No data available to export",
        confirmButtonText: "OK",
      });
      return;
    }

    const exportData = filteredData.map((row, index) => ({
      "S.No.": index + 1,
      "Plan Name": row.planName || "N/A",
      "Cost of Plan": row.initialPlanCost || "0",
      "Initial Recharge": `${row.initialCallMinutes || "0"} minutes, ${row.initialChats || "0"} chats`,
      "Usage": row.usageData || "No data available",
      "Balance": row.balanceData || "No data available",
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths for better readability
    const columnWidths = [
      { wch: 8 },   // S.No.
      { wch: 15 },  // Plan Name
      { wch: 15 },  // Cost of Plan
      { wch: 30 },  // Initial Recharge
      { wch: 30 },  // Usage
      { wch: 30 },  // Balance
    ];
    worksheet['!cols'] = columnWidths;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Usage Report"
    );
    XLSX.writeFile(workbook, "usage_report.xlsx");
  };

  const exportToPdf = () => {
     if (filteredData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data Found",
        text: t("no_data_export_message") || "No data available to export",
        confirmButtonText: "OK",
      });
      return;
    }

    const doc = new jsPDF();
    doc.text("Usage Report", 20, 10); // Hardcoded English title
    const head = [
      [
        "S.No.",
        "Plan Name",
        "Cost of Plan",
        "Initial Recharge",
        "Usage",
        "Balance",
      ], // Hardcoded English headers
    ];
    const body = filteredData.map((row, index) => [
      index + 1,
      row.planName || "N/A",
      row.initialPlanCost || "0",
      `${row.initialCallMinutes || "0"} minutes, ${row.initialChats || "0"} chats`,
      row.usageData || "No data available",
      row.balanceData || "No data available",
    ]);
    doc.autoTable({ head, body });
    doc.save("usage_report.pdf"); // Hardcoded English filename
  };

  return (
    <div className="dashboard">
      <ReportHeader
        reportType={reportType}
        setReportType={setReportType}
        onApply={handleApply}
      />

      {/* Date Range Selector */}
      <div
        className="date-range-container"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          backgroundColor: "#e0e0e0",
          padding: "10px",
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable
            dateFormat={t("date_format")}
            maxDate={new Date()} // Prevents future date selection
          />
          <button className="apply-btn" onClick={handleApply}>
            {t("apply_button")}
          </button>
        </div>
        {/* Export Buttons */}
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            className="export-btn"
            onClick={exportToExcel}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "10px 15px",
              border: "1px solid green",
              borderRadius: "5px",
              backgroundColor: "#f0f9f0",
              color: "green",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              transition: "background-color 0.3s ease, color 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "green";
              e.target.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f0f9f0";
              e.target.style.color = "green";
            }}
          >
            {t("export_to_excel")}
            <i className="fas fa-file-excel" style={{ fontSize: "18px" }}></i>
          </button>

          <button
            className="export-btn export-btn-pdf"
            onClick={exportToPdf}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              padding: "10px 15px",
              border: "1px solid red",
              borderRadius: "5px",
              backgroundColor: "#fff0f0",
              color: "red",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              transition: "background-color 0.3s ease, color 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "red";
              e.target.style.color = "white";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#fff0f0";
              e.target.style.color = "red";
            }}
          >
            {t("export_to_pdf")}
            <i className="fas fa-file-pdf" style={{ fontSize: "18px" }}></i>
          </button>
        </div>
      </div>

      {/* Dynamic Content Section */}
      {content || <UsageReportTable filteredData={filteredData} />}
    </div>
  );
};

export default UsageReport;

